import { FC } from "react";
import {
  Box,
  Group,
  UnstyledButton,
  Text,
  useMantineTheme,
  Menu,
} from "@mantine/core";
import {
  IconBallFootball,
  IconCalendarClock,
  IconShirtSport,
  IconSoccerField,
  IconTable,
} from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import { getPresentationParamsForOrigin } from "../../../../getPresentationParamsForOrigin";

interface BottomNavItemProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  to: string;
}

const BottomNavItem: FC<BottomNavItemProps> = ({ icon, label, active, to }) => {
  return (
    <UnstyledButton
      component={Link}
      to={to}
      style={{
        flex: "1 1 0",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        padding: "8px 4px",
        borderRadius: 8,
        transition: "all 150ms ease",
        color: active ? "white" : "rgba(255, 255, 255, 0.6)",
        textDecoration: "none",
      }}
    >
      {icon}
      <Text
        size="xs"
        fw={active ? 600 : 400}
        style={{
          transition: "all 150ms ease",
        }}
      >
        {label}
      </Text>
    </UnstyledButton>
  );
};

export const BottomNav: FC = () => {
  const location = useLocation();
  const theme = useMantineTheme();
  const { leaderboardPages } = getPresentationParamsForOrigin();
  const hasMultipleLeaderboards = leaderboardPages.length > 1;
  const firstLeaderboard = leaderboardPages[0];

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const leaderboardActive = isActive("/leaderboard");

  return (
    <Box
      hiddenFrom="sm"
      pos="fixed"
      bottom={0}
      left={0}
      right={0}
      style={{
        background: `linear-gradient(135deg, ${theme.colors.fwcBlue[7]} 0%, ${theme.colors.fwcBlue[9]} 100%)`,
        zIndex: 100,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        paddingInlineStart: "env(safe-area-inset-left, 0px)",
        paddingInlineEnd: "env(safe-area-inset-right, 0px)",
      }}
    >
      <Group gap={0} py="xs" px="xs" wrap="nowrap">
        <BottomNavItem
          icon={<IconSoccerField size={22} stroke={1.5} />}
          label="Start"
          active={isActive("/")}
          to="/"
        />
        <BottomNavItem
          icon={<IconCalendarClock size={22} stroke={1.5} />}
          label="Spiele"
          active={isActive("/games")}
          to="/games"
        />
        <BottomNavItem
          icon={<IconShirtSport size={22} stroke={1.5} />}
          label="Teams"
          active={isActive("/teams")}
          to="/teams"
        />
        <BottomNavItem
          icon={<IconBallFootball size={22} stroke={1.5} />}
          label="Topscorer"
          active={isActive("/topscorer")}
          to="/topscorer"
        />

        {hasMultipleLeaderboards ? (
          <Menu position="top" shadow="md" width={200}>
            <Menu.Target>
              <UnstyledButton
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  padding: "8px 4px",
                  borderRadius: 8,
                  transition: "all 150ms ease",
                  color: leaderboardActive
                    ? "white"
                    : "rgba(255, 255, 255, 0.6)",
                }}
              >
                <IconTable size={22} stroke={1.5} />
                <Text
                  size="xs"
                  fw={leaderboardActive ? 600 : 400}
                  style={{ transition: "all 150ms ease" }}
                >
                  Tabelle
                </Text>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {leaderboardPages.map((page) => (
                <Menu.Item
                  key={page.key}
                  component={Link}
                  to={`/leaderboard/${page.key}`}
                  leftSection={<IconTable size={16} />}
                  style={{
                    fontWeight:
                      location.pathname === `/leaderboard/${page.key}`
                        ? 600
                        : 400,
                    color:
                      location.pathname === `/leaderboard/${page.key}`
                        ? theme.colors.fwcBlue[6]
                        : undefined,
                  }}
                >
                  {page.title}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ) : (
          firstLeaderboard && (
            <BottomNavItem
              icon={<IconTable size={22} stroke={1.5} />}
              label="Tabelle"
              active={leaderboardActive}
              to={`/leaderboard/${firstLeaderboard.key}`}
            />
          )
        )}
      </Group>
    </Box>
  );
};
